import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps } from 'formik';
import { get, kebabCase } from 'lodash';

import { getInputClasses, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';

import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';

import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';

import { ErrorNote } from './ErrorNote';
import { Label } from './Label';

type Props = FieldProps & {
  air360ClassName: string;
  availableCountryCodes?: Array<string>;
  countryFieldName?: string;
  customClasses?: string;
  customId?: string;
  dataTestId?: string;
  hideErrorMessage: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  labelAdditionalInfo?: React.ReactNode;
  phoneCodeFieldName: string;
  phoneCodeLabel?: string;
  phoneCodeOptions: Array<{
    countryCode?: string;
    name: string;
    shortName?: string;
    value: string;
  }>;
  phoneCodePlaceholder?: string;
};

export const TelWithCodeInput = ({
  air360ClassName,
  availableCountryCodes,
  countryFieldName,
  customClasses = '',
  customId,
  dataTestId,
  field,
  form,
  hideErrorMessage,
  isDisabled = false,
  isRequired = false,
  label,
  labelAdditionalInfo,
  phoneCodeFieldName,
  phoneCodeLabel,
  phoneCodeOptions,
  phoneCodePlaceholder,
  ...props
}: Props) => {
  const phoneValue = get(form.values, field.name, '');
  const phoneCodeValue = get(form.values, phoneCodeFieldName, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value: phoneValue },
    form,
    isDisabled,
  );

  const errorNoteId = `error-${kebabCase(field.name)}`;

  const classes = getInputClasses({
    hasHeight: true,
    hasValidationStatus: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });

  const inputStyle = customClasses
    ? classnames(air360ClassName, customClasses)
    : classnames(air360ClassName, classes);

  const selectStyle = customClasses ? customClasses : classes;

  const selectedOption = phoneCodeOptions.find((opt) => opt.value === phoneCodeValue);

  const displayValue = selectedOption?.shortName || phoneCodeValue;

  const handlePhoneCodeChange = (e: React.SyntheticEvent<HTMLSelectElement>) => {
    const selectedValue = e.currentTarget.value;
    const selectedOpt = phoneCodeOptions.find((opt) => opt.value === selectedValue);

    form.setFieldValue(phoneCodeFieldName, selectedValue);

    if (countryFieldName) {
      if (selectedOpt?.countryCode) {
        if (
          !availableCountryCodes ||
          availableCountryCodes.length === 0 ||
          availableCountryCodes.includes(selectedOpt.countryCode)
        ) {
          form.setFieldValue(countryFieldName, selectedOpt.countryCode);
        }
      } else if (selectedValue === '') {
        form.setFieldValue(countryFieldName, '');
      }
    }
  };

  const handlePhoneChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    field.onChange(e);
  };

  return (
    <Fragment>
      <div className="flex w-full items-start gap-10">
        {/* Phone Code Select */}
        <div className="relative isolate flex flex-1 flex-col">
          {phoneCodeLabel && (
            <Label
              className="mx-15 mb-5"
              htmlFor={phoneCodeFieldName}
              isErrored={false}
              isRequired={false}
              label={phoneCodeLabel}
            />
          )}
          <div className="relative isolate flex items-center">
            <select
              aria-describedby={errorNoteId}
              aria-invalid={isErrored}
              className={classnames('h-48 pe-40', selectStyle)}
              disabled={isDisabled}
              name={phoneCodeFieldName}
              onChange={handlePhoneCodeChange}
              value={phoneCodeValue || ''}
              style={{ color: phoneCodeValue ? 'transparent' : undefined }}
            >
              {phoneCodePlaceholder && (
                <option value="" disabled>
                  {phoneCodePlaceholder}
                </option>
              )}
              {phoneCodeOptions.map(({ name, value }) => (
                <option key={value} value={value} style={{ color: 'initial' }}>
                  {name}
                </option>
              ))}
            </select>
            {phoneCodeValue && (
              <div className="pointer-events-none absolute inset-0 flex items-center pe-40 ps-15">
                <span className={classnames({ 'text-gray-400': isDisabled })}>{displayValue}</span>
              </div>
            )}
            <div className="pointer-events-none absolute end-0 mx-20 flex items-center">
              <Icon name={ICONS.ARROW_DOWN} width=".625rem" />
            </div>
          </div>
        </div>
        {/* Phone Number Input */}
        <div className="relative flex flex-2 flex-col">
          {label && (
            <Label
              className="mx-15 mb-5"
              htmlFor={customId || field.name}
              isErrored={false}
              isRequired={false}
              label={label}
            />
          )}
          <div className="relative flex items-center">
            <input
              aria-describedby={errorNoteId}
              aria-invalid={isErrored}
              aria-required={isRequired}
              autoComplete="tel"
              className={inputStyle}
              data-testid={dataTestId}
              disabled={isDisabled}
              id={customId ? customId : field.name}
              type="tel"
              {...field}
              onChange={handlePhoneChange}
              value={phoneValue || ''}
              {...props}
            />
            {!isDisabled && (
              <ImageValidationStatus
                className="absolute end-0 mx-20"
                isErrored={isErrored}
                isValidated={isValidated}
              />
            )}
          </div>
        </div>
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          className="mx-15"
          isErrored={isErrored}
          errorMessage={get(form.errors, `${field.name}`)}
          errorNoteId={errorNoteId}
        />
      )}
    </Fragment>
  );
};
